// Cloud TTS speaker for Ask BigAgri — natural voices via the ask-tts relay
// (OpenAI, a disclosed sub-processor; only ANSWER TEXT is sent, never audio).
// Implements the same SentenceSpeaker contract as the device speaker and
// FALLS BACK to it on any failure (unset key, cap, network, playback) — the
// worst case is yesterday's voice, never silence.
//
// Batching: streamed answer text accumulates; whenever the player is idle,
// everything currently complete is synthesised as ONE request. The first
// sentence therefore starts fast (~sub-second) and long answers settle into
// a few larger requests instead of one round-trip per sentence.
//
// iOS: primeCloudAudio() must run inside a user gesture once per page — it
// unlocks the shared <audio> element the way primeSpeechOutput() unlocks
// speechSynthesis. One shared element, one active speaker at a time (barge-in
// cancels the old one before a new ask starts).

import { supabase } from '@/lib/supabase'
import { chunkCompleteSentences, prepareTextForSpeech, type SentenceSpeaker } from '@/lib/speech'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string

// 44-byte silent WAV — playing it inside the gesture unlocks the element.
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA='

let sharedAudio: HTMLAudioElement | null = null
let unlocked = false

export function primeCloudAudio(): void {
  try {
    if (!sharedAudio) sharedAudio = new Audio()
    sharedAudio.src = SILENT_WAV
    void sharedAudio.play().then(() => { unlocked = true }).catch(() => { /* not fatal — fallback covers it */ })
  } catch { /* no Audio support → device fallback */ }
}

export function cloudTtsPrimed(): boolean {
  return unlocked
}

async function postTts(body: Record<string, unknown>): Promise<Response | null> {
  try {
    const { data: sess } = await supabase.auth.getSession()
    const token = sess?.session?.access_token
    if (!token) return null
    return await fetch(`${SUPABASE_URL}/functions/v1/ask-tts`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_ANON, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    return null
  }
}

async function fetchTtsClip(text: string): Promise<string | null> {
  const res = await postTts({ text })
  if (!res || !res.ok) return null
  try {
    return URL.createObjectURL(await res.blob())
  } catch {
    return null
  }
}

// Fire on the ask GESTURE: runs the relay's full auth/entitlement gate and
// boots the isolate while the model is still composing the answer, so the
// FIRST real clip request skips the cold path — that gate chain is otherwise
// pure dead air between the text appearing and the voice starting.
let lastWarmAt = 0
export function prewarmCloudTts(): void {
  const now = Date.now()
  if (now - lastWarmAt < 60_000) return
  lastWarmAt = now
  void postTts({ warm: true })
}

// createCloudSpeaker — cloud-first SentenceSpeaker. `makeFallback` builds the
// device speaker lazily; after the first failure ALL remaining text (unspoken
// buffer included) routes through it so mid-answer degradation is seamless.
export function createCloudSpeaker(
  opts: { rate?: number } | undefined,
  makeFallback: () => SentenceSpeaker,
): SentenceSpeaker {
  let buffer = ''
  let flushed = false
  let cancelled = false
  let fetching = false
  let playing = false
  let failed = false
  let pendingClip: string | null = null
  let fallback: SentenceSpeaker | null = null
  const rate = Math.min(1.6, Math.max(0.7, opts?.rate ?? 1.0))

  const failOver = () => {
    if (cancelled || fallback) return
    fallback = makeFallback()
    if (buffer) fallback.feed(buffer)
    buffer = ''
    if (flushed) fallback.flush()
  }

  // A prefetch failure must wait for the playing clip to finish before the
  // device voice takes over, or the two would talk over each other.
  const maybeFailOver = () => {
    if (failed && !playing) { failed = false; failOver() }
  }

  // Take everything currently speakable: complete sentences, or the whole
  // tail once flushed. The FIRST batch is capped at one sentence — the first
  // clip must be small so it downloads and speaks fast; the pipeline hides
  // the latency of every later batch.
  let firstBatch = true
  const takeBatch = (): string => {
    const { complete, rest } = chunkCompleteSentences(buffer)
    if (firstBatch && complete.length > 0) {
      firstBatch = false
      // \n rejoins keep each deferred sentence's boundary intact on re-chunk.
      buffer = [...complete.slice(1), rest].filter(Boolean).join('\n')
      return prepareTextForSpeech(complete[0])
    }
    let batch = complete.join(' ')
    buffer = rest
    if (flushed && buffer.trim()) { batch = `${batch} ${buffer}`.trim(); buffer = '' }
    if (batch) firstBatch = false
    return prepareTextForSpeech(batch)
  }

  // Two-stage pipeline: at most one clip PLAYING and one clip FETCHING at any
  // moment — the next batch synthesises while the current one speaks, so
  // batches join without a silent fetch gap in between. The guard set IS the
  // pipeline invariant — scattering it would hide it.
  // fallow-ignore-next-line complexity
  const fill = async (): Promise<void> => {
    if (cancelled || fallback || failed || fetching || pendingClip) return
    const batch = takeBatch()
    if (!batch) return
    fetching = true
    const clip = await fetchTtsClip(batch)
    fetching = false
    if (cancelled) { if (clip) URL.revokeObjectURL(clip); return }
    if (!clip || !sharedAudio) {
      // Refeed the unspoken batch so the device voice picks it up verbatim.
      buffer = buffer ? `${batch} ${buffer}` : batch
      failed = true
      maybeFailOver()
      return
    }
    pendingClip = clip
    playNext()
  }

  const playNext = (): void => {
    if (cancelled || fallback || playing || !pendingClip || !sharedAudio) return
    const clip = pendingClip
    pendingClip = null
    playing = true
    const audio = sharedAudio
    const settle = (err: boolean) => {
      URL.revokeObjectURL(clip)
      playing = false
      if (err) failed = true
      maybeFailOver()
      if (!fallback) { playNext(); void fill() }
    }
    audio.src = clip
    audio.playbackRate = rate
    audio.onended = () => settle(false)
    audio.onerror = () => settle(true)
    audio.play().catch(() => settle(true))
    void fill() // prefetch the next batch while this one speaks
  }

  return {
    feed(delta: string) {
      if (cancelled) return
      if (fallback) { fallback.feed(delta); return }
      buffer += delta
      void fill()
    },
    flush() {
      if (cancelled) return
      flushed = true
      if (fallback) { fallback.flush(); return }
      void fill()
    },
    cancel() {
      cancelled = true
      fallback?.cancel()
      if (pendingClip) { URL.revokeObjectURL(pendingClip); pendingClip = null }
      if (sharedAudio) {
        sharedAudio.onended = null
        try { sharedAudio.pause() } catch { /* already stopped */ }
      }
    },
  }
}
